import { useMutation } from "@tanstack/react-query";
import { post } from "api/api-methods";
import { useBookingId } from "hooks/use-booking-id";
import { trackEvent } from "logger/logger";
import { pageTrackingService, TRACKING_VERSION } from "logger/page-tracking";
import queryClient from "query-client";

type CancelBookingParams = {
    id?: string;
    systemTriggered?: boolean;
};

export const useCancelBooking = () => {
    const pathBookingId = useBookingId();
    const cancelBooking = async ({ id, systemTriggered = false }: CancelBookingParams) => {
        const bookingId = id || pathBookingId;
        if (!bookingId) return;

        const startTime = new Date().getTime();

        try {
            await post(`WebBooking/WebBookings/${bookingId}/Cancel`, { systemTriggered });

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            const pageContext = pageTrackingService.getCurrentContext();

            // refetch availableTimes queries to ensure data is in sync
            await queryClient.refetchQueries({
                queryKey: ["availableTimes"],
            });

            // Track booking cancellation success
            trackEvent("Booking.Cancelled.Success", {
                bookingId,
                sessionId: pageContext.sessionId,
                durationMs: loadTime,
                trackingVersion: TRACKING_VERSION,
            });
        } catch (error) {
            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            const pageContext = pageTrackingService.getCurrentContext();

            // Track booking cancellation failure
            trackEvent("Booking.Cancelled.Failure", {
                bookingId,
                sessionId: pageContext.sessionId,
                durationMs: loadTime,
                errorMessage: error instanceof Error ? error.message : "Unknown error",
                trackingVersion: TRACKING_VERSION,
            });

            throw error;
        }
    };

    return useMutation({
        mutationFn: cancelBooking,
        scope: {
            id: "webbooking-create",
        },
    });
};
